import { useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Inference, InferenceCollection } from "../../../types/inferences";

type UseValidateNavigationOptions = {
  sceneId: string | undefined;
  inferenceId: string | undefined;
  inferences: InferenceCollection | undefined;
  inferencesIsPending: boolean;
  compositeMode: boolean;
};

// Sort key: left→right by xmin, then top→bottom by ymin within each column.
// This matches the physical chip layout across the scene so next/previous
// navigation moves spatially rather than jumping by confidence or insertion order.
function sortByPixelPosition(a: Inference, b: Inference) {
  const ac = a.properties.inference.pixel_coords;
  const bc = b.properties.inference.pixel_coords;
  return ac.xmin !== bc.xmin ? ac.xmin - bc.xmin : ac.ymin - bc.ymin;
}

// Returns the stable URL anchor for an inference: the first member inference_id
// in composite mode (stable across re-compositing), or the inference's own id.
function getAnchorId(inf: Inference, compositeMode: boolean) {
  return compositeMode
    ? (inf.properties.inference_ids?.[0] ?? inf.properties.ids?.[0])
    : inf.properties.id;
}

function useValidateNavigation({
  sceneId,
  inferenceId,
  inferences,
  inferencesIsPending,
  compositeMode,
}: UseValidateNavigationOptions) {
  const navigate = useNavigate();

  // Track the last known pixel position so we can find the nearest chip when
  // the current anchor ID stops matching after a mode switch or re-compositing.
  const lastPixelCoords = useRef<{ xmin: number; ymin: number } | null>(null);

  const inferencesSorted = useMemo(() => {
    if (!inferences || inferencesIsPending) return [];
    return [...inferences.features].sort(sortByPixelPosition);
  }, [inferences, inferencesIsPending]);

  // The URL always carries an individual inference `id` as the anchor — stable regardless
  // of composite membership changes (re-compositing, validation, mode switches).
  // In composite mode we find the composite whose inference_ids contains the anchor;
  // in regular mode we match directly on id.
  const anchorId = Number(inferenceId);
  const currentIndex = inferencesSorted.findIndex(inf =>
    compositeMode
      ? inf.properties.inference_ids?.includes(anchorId)
      : inf.properties.id === anchorId
  );
  const selectedInference = inferencesSorted[currentIndex];

  // Keep lastPixelCoords in sync whenever we have a valid selection
  useEffect(() => {
    if (selectedInference) {
      const { xmin, ymin } = selectedInference.properties.inference.pixel_coords;
      lastPixelCoords.current = { xmin, ymin };
    }
  }, [selectedInference]);

  // Navigate when the URL has no ID or the anchor no longer matches the current list
  useEffect(() => {
    if (inferencesSorted.length === 0 || !sceneId) return;
    if (inferenceId && currentIndex !== -1) return;

    // Find the nearest chip to where the user was — preserves position across mode switches.
    // Falls back to the first item on fresh page load.
    const prev = lastPixelCoords.current;
    const target = prev
      ? inferencesSorted.reduce((closest, inf) => {
        const { xmin, ymin } = inf.properties.inference.pixel_coords;
        const { xmin: cx, ymin: cy } = closest.properties.inference.pixel_coords;
        const dCur = (xmin - prev.xmin) ** 2 + (ymin - prev.ymin) ** 2;
        const dBest = (cx - prev.xmin) ** 2 + (cy - prev.ymin) ** 2;
        return dCur < dBest ? inf : closest;
      })
      : inferencesSorted[0];

    navigate(`/scenes/${sceneId}/validate/${getAnchorId(target, compositeMode)}`, { replace: true });
  }, [inferenceId, inferencesSorted, currentIndex, navigate, sceneId, compositeMode]);

  const nextInferenceId: number | false = (currentIndex < inferencesSorted.length - 1
    && getAnchorId(inferencesSorted[currentIndex + 1], compositeMode)) ?? false;

  const previousInferenceId: number | false = (currentIndex > 0
    && getAnchorId(inferencesSorted[currentIndex - 1], compositeMode)) ?? false;

  return {
    inferencesSorted,
    currentIndex,
    selectedInference,
    nextInferenceId,
    previousInferenceId,
  };
}

export default useValidateNavigation;
